import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOVEMENT_TYPES, PURPOSES, UNITS, VEHICLE_RE } from "@/config/gatepass";
import PartyCombobox from "@/components/PartyCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyMaterial = () => ({ description: "", quantity: "", unit: "MT", remarks: "" });

function useIstClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const date = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(now);
  return { date, time };
}

export default function GatePassEditor({ embedded = false, onDone }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const clock = useIstClock();
  const [form, setForm] = useState({
    movement_type: "Outward", purpose: "Sale", purpose_other: "", party_name: "",
    vehicle_number: "", vehicle_exception: false, driver_name: "", driver_mobile: "",
    bill_number: "", remarks: "", materials: [emptyMaterial()],
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      api.get(`/gate-passes/${id}`).then((r) => {
        const g = r.data;
        if (g.status !== "draft") { toast.error("Only drafts can be edited"); navigate(`/gate-pass/${id}`); return; }
        setForm({
          movement_type: g.movement_type || "Outward", purpose: g.purpose || "Sale",
          purpose_other: g.purpose_other || "", party_name: g.party_name || "",
          vehicle_number: g.vehicle_number || "", vehicle_exception: !!g.vehicle_exception,
          driver_name: g.driver_name || "", driver_mobile: g.driver_mobile || "",
          bill_number: g.bill_number || "", remarks: g.remarks || "",
          materials: (g.materials && g.materials.length) ? g.materials.map((m) => ({ description: m.description || "", quantity: m.quantity ?? "", unit: m.unit || "MT", remarks: m.remarks || "" })) : [emptyMaterial()],
        });
      }).catch(() => toast.error("Could not load gate pass")).finally(() => setLoading(false));
    }
  }, [id]); // eslint-disable-line

  if (!can("gate_pass", "create"))
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="restricted-notice">
        <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
        <h2 className="font-display text-xl font-bold">Access Restricted</h2>
        <p className="text-muted-foreground text-sm mt-2">You don't have permission to create gate passes.</p>
      </div>
    );

  if (id && !isAdmin)
    return (
      <div className="max-w-md mx-auto mt-16 text-center" data-testid="edit-restricted-notice">
        <Icons.Lock className="h-8 w-8 mx-auto text-destructive mb-3" />
        <h2 className="font-display text-xl font-bold">Editing is Admin-only</h2>
        <p className="text-muted-foreground text-sm mt-2">Only an administrator can edit an existing gate pass. Please ask an admin, or create a new gate pass.</p>
        <Button variant="outline" className="mt-4 gap-1.5" onClick={() => navigate(`/gate-pass/${id}`)} data-testid="edit-restricted-back"><Icons.ArrowLeft className="h-4 w-4" />Back to Gate Pass</Button>
      </div>
    );

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setMat = (i, k, v) => setForm((p) => ({ ...p, materials: p.materials.map((m, idx) => idx === i ? { ...m, [k]: v } : m) }));
  const addMat = () => setForm((p) => ({ ...p, materials: [...p.materials, emptyMaterial()] }));
  const removeMat = (i) => setForm((p) => ({ ...p, materials: p.materials.filter((_, idx) => idx !== i) }));

  const vehicleClean = (form.vehicle_number || "").toUpperCase().replace(/\s+/g, "");
  const vehicleValid = form.vehicle_exception || VEHICLE_RE.test(vehicleClean) || vehicleClean === "";

  const validate = () => {
    if (!form.party_name.trim()) return "Party / Customer name is required";
    if (form.purpose === "Other" && !form.purpose_other.trim()) return "Please specify the purpose for 'Other'";
    if (!vehicleClean) return "Vehicle number is required";
    if (!vehicleValid) return "Invalid Indian vehicle number. Tick 'special / non-standard vehicle' to override.";
    if (!form.driver_name.trim()) return "Driver name is required";
    const mob = form.driver_mobile.replace(/[\s-]/g, "");
    if (!/^\+?[0-9]{7,15}$/.test(mob)) return "Invalid driver mobile number";
    const mats = form.materials.filter((m) => m.description.trim());
    if (!mats.length) return "At least one material entry is required";
    return null;
  };

  const payload = () => ({ ...form, vehicle_number: vehicleClean, materials: form.materials.filter((m) => m.description.trim()) });

  const save = async (submit) => {
    if (submit) { const err = validate(); if (err) { toast.error(err); return; } }
    setSaving(true);
    try {
      let gpId = id;
      if (id) { await api.put(`/gate-passes/${id}`, payload()); }
      else { const r = await api.post("/gate-passes", payload()); gpId = r.data.id; }
      if (submit) {
        await api.post(`/gate-passes/${gpId}/submit`);
        toast.success("Submitted for verification");
      } else {
        toast.success("Draft saved");
      }
      navigate(`/gate-pass/${gpId}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center mt-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      {!embedded && (
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/gate-pass")} data-testid="gp-back-btn"><Icons.ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{id ? "Edit Gate Pass Draft" : "Create Gate Pass"}</h1>
            <p className="text-sm text-muted-foreground">Fill in movement details. Gate pass number is assigned on final authorization.</p>
          </div>
        </div>
      )}

      {/* Auto number/date/time bar */}
      <Card className="p-4" style={{ borderColor: "#1e3a5f", borderWidth: 1 }} data-testid="gp-meta-bar">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div><div className="overline text-muted-foreground">Gate Pass No.</div><div className="font-semibold" style={{ color: "#1e3a5f" }}>Assigned on issue</div></div>
          <div><div className="overline text-muted-foreground">Date (IST)</div><div className="font-semibold">{clock.date}</div></div>
          <div><div className="overline text-muted-foreground">Time (IST)</div><div className="font-semibold tabular-nums">{clock.time}</div></div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Movement Type *</Label>
            <Select value={form.movement_type} onValueChange={(v) => set("movement_type", v)}>
              <SelectTrigger data-testid="gp-field-movement_type"><SelectValue /></SelectTrigger>
              <SelectContent>{MOVEMENT_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Purpose *</Label>
            <Select value={form.purpose} onValueChange={(v) => set("purpose", v)}>
              <SelectTrigger data-testid="gp-field-purpose"><SelectValue /></SelectTrigger>
              <SelectContent>{PURPOSES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.purpose === "Other" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="overline text-muted-foreground">Specify Purpose *</Label>
              <Input value={form.purpose_other} onChange={(e) => set("purpose_other", e.target.value)} data-testid="gp-field-purpose_other" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Party / Customer Name *</Label>
            <PartyCombobox value={form.party_name} onChange={(v) => set("party_name", v)} testid="gp-field-party_name" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Vehicle Number *</Label>
            <Input value={form.vehicle_number} onChange={(e) => set("vehicle_number", e.target.value.toUpperCase().replace(/\s+/g, ""))}
              placeholder="e.g. CG04AB1234" className={!vehicleValid ? "border-destructive" : ""} data-testid="gp-field-vehicle_number" />
            {!vehicleValid && <p className="text-xs text-destructive">Invalid Indian format — tick override for special vehicles.</p>}
            <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
              <Checkbox checked={form.vehicle_exception} onCheckedChange={(v) => set("vehicle_exception", !!v)} data-testid="gp-field-vehicle_exception" />
              Special / non-standard vehicle number
            </label>
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Driver Name *</Label>
            <Input value={form.driver_name} onChange={(e) => set("driver_name", e.target.value)} data-testid="gp-field-driver_name" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Driver Mobile Number *</Label>
            <Input type="tel" value={form.driver_mobile} onChange={(e) => set("driver_mobile", e.target.value)} placeholder="+91 98765 43210" data-testid="gp-field-driver_mobile" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">Bill / Invoice Number</Label>
            <Input value={form.bill_number} onChange={(e) => set("bill_number", e.target.value)} data-testid="gp-field-bill_number" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="overline text-muted-foreground">Remarks</Label>
            <Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} data-testid="gp-field-remarks" />
          </div>
        </div>
      </Card>

      {/* Materials */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold">Material Details *</h3>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addMat} data-testid="gp-add-material"><Icons.Plus className="h-4 w-4" />Add Material</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>{["Sl.", "Material Description", "Quantity", "Unit", "Remarks", ""].map((h, i) => <TableHead key={i} className="overline text-muted-foreground">{h}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {form.materials.map((m, i) => (
                <TableRow key={i}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell><Input value={m.description} onChange={(e) => setMat(i, "description", e.target.value)} placeholder="Material name" data-testid={`gp-mat-desc-${i}`} /></TableCell>
                  <TableCell className="w-28"><Input type="number" value={m.quantity} onChange={(e) => setMat(i, "quantity", e.target.value)} data-testid={`gp-mat-qty-${i}`} /></TableCell>
                  <TableCell className="w-24">
                    <Select value={m.unit} onValueChange={(v) => setMat(i, "unit", v)}>
                      <SelectTrigger data-testid={`gp-mat-unit-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Input value={m.remarks} onChange={(e) => setMat(i, "remarks", e.target.value)} data-testid={`gp-mat-remarks-${i}`} /></TableCell>
                  <TableCell>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" disabled={form.materials.length === 1} onClick={() => removeMat(i)} data-testid={`gp-mat-remove-${i}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="gp-save-draft-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save as Draft
        </Button>
        <Button onClick={() => save(true)} disabled={saving} className="active:scale-95 transition-transform" data-testid="gp-submit-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save &amp; Submit for Verification
        </Button>
        <Button variant="ghost" onClick={() => (embedded && onDone ? onDone() : navigate("/gate-pass"))} data-testid="gp-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}
